import { HttpClient } from '@adapters/HttpClient';
import {
  TagModel,
  CreateTagRequest,
  UpdateTagRequest,
  ModifyOrderTagsRequest,
} from '../models/CRM';

export interface TagsListParams {
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface TagsPage {
  tags: TagModel[];
  total: number;
  currentPage: number;
  lastPage: number;
}

/** Estructura de la respuesta GET /crm/tags (success, data, meta, links, filters) */
interface TagsListResponse {
  success?: boolean;
  data: TagModel[];
  meta?: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from?: number;
    to?: number;
  };
  links?: { first?: string; prev?: string | null; next?: string | null; last?: string };
  filters?: { active_only?: boolean; search?: string | null; sort_by?: string; sort_order?: string };
}

/**
 * Adaptador para la API de tags CRM.
 * Documentación: GET/POST/PUT/DELETE /api/v1/crm/tags, POST /api/v1/crm/orders/tags/modify
 */
export class TagsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Lista tags. Por ahora solo page y per_page (llamada mínima).
   */
  async getTags(params: TagsListParams = {}): Promise<TagsPage> {
    const { page = 1, per_page = 15, sort_by, sort_order } = params;

    const queryParams: Record<string, number | string> = {
      page,
      per_page: Math.min(Math.max(per_page, 1), 100),
    };
    if (sort_by) queryParams.sort_by = sort_by;
    if (sort_order) queryParams.sort_order = sort_order;

    // Respuesta: { success, data: TagModel[], meta: { total, per_page, current_page, last_page, from, to }, links?, filters? }
    const response = await this.httpClient.get<TagsListResponse>(
      '/crm/tags',
      queryParams,
      {}
    );

    const tags = Array.isArray(response?.data) ? response.data : [];
    const meta = response?.meta ?? {};
    const currentPage = meta.current_page ?? meta.currentPage ?? 1;
    const lastPage = meta.last_page ?? meta.lastPage ?? 1;
    const total = meta.total ?? tags.length;

    return {
      tags,
      total,
      currentPage,
      lastPage,
    };
  }

  async getTag(tagId: number): Promise<TagModel | null> {
    const response = await this.httpClient.get<{ data: TagModel }>(
      `/crm/tags/${tagId}`,
      {},
      {}
    );
    return response?.data ?? null;
  }

  /**
   * Crea un tag. La API responde 201 No Content (sin body).
   * Se obtiene el tag recién creado listando los más recientes (sort id desc) y buscando por nombre.
   */
  async createTag(data: CreateTagRequest): Promise<TagModel> {
    const nameTrimmed = data.name.trim();
    await this.httpClient.post<void>(
      '/crm/tags',
      {
        name: nameTrimmed,
        ...(data.color && { color: data.color }),
        ...(data.description && { description: data.description }),
      }
    );

    // Buscar el tag recién creado: pedir los más recientes primero (sort_by admite: name, created_at, description)
    const { tags } = await this.getTags({
      per_page: 50,
      sort_by: 'created_at',
      sort_order: 'desc',
    });
    const created = tags.find(
      (t) => (t.name ?? '').trim().toLowerCase() === nameTrimmed.toLowerCase()
    );
    if (created) return created;

    // Si la API no devuelve el tag en la primera página (p. ej. sin sort), devolver
    // un placeholder para que la mutación sea éxito y refetch() cargue la lista real
    return {
      id: 0,
      name: nameTrimmed,
      color: data.color ?? undefined,
      description: data.description,
    } as TagModel;
  }

  async updateTag(tagId: number, data: UpdateTagRequest): Promise<void> {
    await this.httpClient.put(`/crm/tags/${tagId}`, {
      name: data.name,
      ...(data.color !== undefined && { color: data.color }),
      ...(data.description !== undefined && { description: data.description }),
      ...(data.is_active !== undefined && { is_active: data.is_active }),
    });
  }

  async deleteTag(tagId: number): Promise<void> {
    await this.httpClient.delete(`/crm/tags/${tagId}`);
  }

  /**
   * Asociar o desasociar tags a un pedido.
   * Al menos attach_tags o detach_tags debe tener al menos un elemento.
   */
  async modifyOrderTags(data: ModifyOrderTagsRequest): Promise<void> {
    const body: Record<string, number | number[]> = {
      order_id: data.order_id,
    };
    if (data.attach_tags?.length) {
      body.attach_tags = data.attach_tags;
    }
    if (data.detach_tags?.length) {
      body.detach_tags = data.detach_tags;
    }
    await this.httpClient.post('/crm/orders/tags/modify', body);
  }
}
